import React, { FC, ReactNode } from 'react';
import { Box, Divider } from '@wix/design-system';
import { ErrorBoundary } from 'react-error-boundary';
import ErrorFallback from '../ErrorFallback';
import { IHandleBIGenericClick, ReviewData } from '../../common/types';
import Review from './Review';
import ReviewsHeader from './ReviewsHeader';
import RenderLoadingReview from '@components/Reviews/RenderLoadingReview';
import { ReplyProps } from './ReviewReplyDialogue/ReviewReplyDialogue';
import NoReviews from '@components/Reviews/NoReviews';
import { PROFILE_REVIEWS_PER_PAGE } from '@utils/constants';
import { Service } from '@wix/ambassador-category-services/types';

export type ReviewsTextKeys = Record<
  'viewReviewSubjectCTA' | 'replyAuthorTitle' | 'noReviews',
  string
>;

export type ReviewsProps = {
  showReviewsCount?: boolean;
  reviewsCount: number;
  averageReviewRating: number;
  reviews: ReviewData[];
  page?: number;
  i18nKeys: ReviewsTextKeys;
  services?: Service[];
  isMyProfile?: boolean;
  fetchMore?: boolean;
  borderContainerClass?: string;
  marginBottom?: string;
  isMobileView: boolean;
  handleBiGenericClick?: IHandleBIGenericClick;
  loadNewReview?: boolean;
  addNewReviewBanner?: ReactNode;
  replyProps: ReplyProps;
  onDelete?: () => void;
  onEdit?: () => void;
  userReview?: ReviewData | null;
};

const ReviewsWithPaging: FC<ReviewsProps> = ({
  showReviewsCount = true,
  reviewsCount,
  averageReviewRating,
  reviews,
  isMyProfile,
  services,
  borderContainerClass,
  marginBottom,
  isMobileView,
  handleBiGenericClick,
  i18nKeys,
  loadNewReview,
  addNewReviewBanner,
  replyProps,
  onEdit,
  userReview,
}) => {
  return (
    <Box display="block" dataHook="reviews-section" width="100%">
      <ReviewsHeader
        marginBottom={marginBottom}
        showReviewsCount={showReviewsCount}
        isMobileView={isMobileView}
        reviewsCount={reviewsCount}
        averageReviewRating={averageReviewRating}
      />
      {addNewReviewBanner && <Box marginTop="24px">{addNewReviewBanner}</Box>}
      {!loadNewReview && reviews?.length > 0 ? (
        <>
          {isMobileView && showReviewsCount && <Divider />}
          {loadNewReview && <RenderLoadingReview />}
          <Box
            dataHook="reviews-container"
            display="block"
            className={borderContainerClass}
          >
            {reviews.map((review: ReviewData, index: number) => (
              <PaddedReview
                key={review.id}
                review={review}
                isMyProfile={isMyProfile}
                services={services}
                handleBiGenericClick={handleBiGenericClick}
                showDivider={index + 1 < reviews.length}
                i18nKeys={i18nKeys}
                isMobileView={isMobileView}
                replyProps={replyProps}
                onEdit={onEdit}
                userReview={userReview}
              />
            ))}
          </Box>
        </>
      ) : loadNewReview ? (
        <>
          <Box
            display="block"
            className={borderContainerClass}
            paddingBottom="SP4"
          >
            {Array.from({ length: PROFILE_REVIEWS_PER_PAGE }).map((_, i) => (
              <RenderLoadingReview
                key={i}
                hideDivider={i === PROFILE_REVIEWS_PER_PAGE - 1}
              />
            ))}
          </Box>
        </>
      ) : (
        <NoReviews
          hasNegativeMargin={Boolean(borderContainerClass)}
          noReviewsKey={i18nKeys.noReviews}
          isMobileView={isMobileView}
        />
      )}
    </Box>
  );
};

const PaddedReview: FC<
  Pick<
    ReviewsProps,
    | 'isMyProfile'
    | 'services'
    | 'handleBiGenericClick'
    | 'i18nKeys'
    | 'isMobileView'
    | 'replyProps'
    | 'onEdit'
    | 'userReview'
  > & {
    review: ReviewData;
    showDivider: boolean;
  }
> = ({
  review,
  isMyProfile,
  services,
  handleBiGenericClick,
  showDivider,
  i18nKeys,
  isMobileView,
  replyProps,
  onEdit,
  userReview,
}) => {
  return (
    <Box display="block" paddingTop="SP4">
      <ErrorBoundary FallbackComponent={ErrorFallback}>
        <Review
          i18nKeys={i18nKeys}
          review={review}
          isMyProfile={isMyProfile}
          services={services}
          handleBiGenericClick={handleBiGenericClick}
          isMobileView={isMobileView}
          replyProps={replyProps}
          onEdit={onEdit}
          userReview={userReview}
        />
        {showDivider ? (
          <Box display="block" paddingTop="SP4">
            <Divider />
          </Box>
        ) : (
          <Box display="block" paddingTop="SP4"></Box>
        )}
      </ErrorBoundary>
    </Box>
  );
};

export default ReviewsWithPaging;
